import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { selfRegisterRequestSchema } from '@arkitekt/shared';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

interface RegisterForm {
  email: string;
  password: string;
  displayName: string;
}

export function RegisterPage() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterForm>();

  const onSubmit = async (data: RegisterForm) => {
    setError(null);
    setIsLoading(true);

    try {
      selfRegisterRequestSchema.parse(data);
      
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Registrering feilet');
      }

      const result = await response.json();
      setAuth(result.user, result.accessToken, result.refreshToken);
      
      if (result.emailVerificationRequired) {
        navigate('/verify-email-sent');
      } else {
        navigate('/spaces');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrering feilet');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Opprett konto</CardTitle>
          <CardDescription>Registrer deg for å komme i gang med Arkitekt</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="displayName">Navn</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Ditt navn"
                {...register('displayName', { required: 'Navn er påkrevd' })}
              />
              {errors.displayName && (
                <p className="text-sm text-destructive">{errors.displayName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                placeholder="din@epost.no"
                {...register('email', { required: 'E-post er påkrevd' })}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                type="password"
                {...register('password', { 
                  required: 'Passord er påkrevd',
                  minLength: { value: 8, message: 'Passord må være minst 8 tegn' }
                })}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Minst 8 tegn, inkludert stor bokstav, liten bokstav og tall
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Registrerer...' : 'Opprett konto'}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Har du allerede konto?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Logg inn
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
